import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { FadeIn, RevealText } from "../motion/Reveal";
import { SectionLabel } from "../motion/SectionLabel";
import { CTA } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", organization: "", message: "" };

export const FinalCTA = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, inquiry_type: "partnership" });
      setDone(true);
      setForm(initial);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="cta-section"
      className="relative min-h-screen flex flex-col justify-center py-28 md:py-32 px-6 md:px-10 overflow-hidden bg-[#0a0a0a]"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-[#ff4500]/20 blur-[160px]"
        aria-hidden
      />
      <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div>
          <SectionLabel>{CTA.label}</SectionLabel>
          <RevealText
            as="h2"
            lines={["Great stories always", "find their audience."]}
            className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight"
          />
          <FadeIn delay={0.2}>
            <p className="mt-8 font-body text-lg text-neutral-300 leading-relaxed font-light max-w-md">
              {CTA.body}
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <a
              href={`mailto:${CTA.email}`}
              data-testid="cta-email-link"
              className="mt-8 inline-block font-display text-2xl md:text-3xl text-white hover:text-[#ff8c00] transition-colors duration-300"
            >
              {CTA.email}
            </a>
          </FadeIn>
        </div>

        <FadeIn delay={0.15} className="w-full">
          {done ? (
            <div
              data-testid="cta-success"
              className="rounded-3xl border border-[#ff4500]/30 bg-white/[0.03] p-10 md:p-12 flex flex-col items-start gap-5"
            >
              <CheckCircle2 size={40} className="text-[#ff8c00]" />
              <h3 className="font-display font-bold text-white text-3xl tracking-tight">
                Request received.
              </h3>
              <p className="font-body text-neutral-400 leading-relaxed">
                We&apos;ve received your message and a member of the OORU team will be in touch
                shortly.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="cta-reset"
                className="font-body text-sm text-[#ff8c00] hover:text-white transition-colors"
              >
                Send another →
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-10 backdrop-blur-xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Organization">
                  <input
                    data-testid="contact-organization"
                    value={form.organization}
                    onChange={update("organization")}
                    placeholder="Company / Fund / Studio"
                    className={inputCls}
                  />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Message" required>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Tell us how you'd like to partner with OORU."
                    className={`${inputCls} resize-none`}
                  />
                </Field>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-white text-black font-body font-medium px-7 py-4 hover:bg-[#ff4500] hover:text-white transition-[transform,background-color,color,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(255,69,0,0.55)] disabled:opacity-60"
              >
                {loading ? "Sending…" : "Partner With OORU"}
                {!loading && (
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                )}
              </button>
            </form>
          )}
        </FadeIn>
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 font-body text-white placeholder:text-neutral-600 focus:border-[#ff4500] focus:outline-none focus:ring-2 focus:ring-[#ff4500]/30 transition-colors duration-300";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-body text-xs uppercase tracking-[0.18em] text-neutral-500">
      {label}
      {required && <span className="text-[#ff4500]"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);
